// Service de calcul automatique des devis selon le type de projet

/**
 * Calcule la quantité nécessaire pour un produit selon son type et le type de projet
 * @param {string} productType - Type du produit (LAMBOURDE, BANDE_BITUMINEUSE, etc.)
 * @param {string} projectType - Type de projet (TERASSE_EXTERIEUR, BARDAGE, SOL, MUR_PLAQUE_PLATRE)
 * @param {number} surfaceArea - Surface en m²
 * @param {Object} userInputs - Inputs utilisateur par section
 * @returns {number} Quantité calculée
 */
export const calculateProductQuantity = (productType, projectType, surfaceArea, userInputs = {}, selectedChoices = []) => {
  // Récupérer la surface depuis les inputs utilisateur ou utiliser la surface par défaut
  const terraceArea = getTerraceArea(userInputs, projectType) || surfaceArea;
  
  switch (projectType) {
    case 'TERASSE_EXTERIEUR':
      return calculateTerraceQuantity(productType, terraceArea);
    case 'BARDAGE':
      return calculateBardageQuantity(productType, terraceArea, userInputs, selectedChoices);
    case 'SOL':
      return calculateSolQuantity(productType, terraceArea, userInputs, selectedChoices);
    case 'MUR_PLAQUE_PLATRE':
      return calculateMurPlaquePlatreQuantity(productType, terraceArea, userInputs, selectedChoices);
    default:
      return 1; // Quantité par défaut
  }
};

/**
 * Récupère la surface depuis les inputs utilisateur
 * Cherche la surface selon le type de projet (terrasse, mur, etc.)
 */
export const getTerraceArea = (userInputs, projectType) => {
  console.log('Recherche de la surface dans userInputs:', userInputs, 'pour le type:', projectType);
  
  // Mots-clés à chercher selon le type de projet
  const searchKeywords = {
    'TERASSE_EXTERIEUR': ['taille', 'terrasse', 'surface'],
    'MUR_PLAQUE_PLATRE': ['surface', 'm²', 'm2', 'mur', 'plaque'],
    'BARDAGE': ['surface', 'm²', 'm2', 'bardage'],
    'SOL': ['surface', 'm²', 'm2', 'sol']
  };
  
  const keywords = searchKeywords[projectType] || ['surface', 'm²', 'm2'];
  
  // Pour tous les types de projets, chercher la surface dans les inputs
  for (const [sectionId, sectionInputs] of Object.entries(userInputs)) {
    console.log('Vérification de la section:', sectionId, 'avec inputs:', sectionInputs);
    
    // Chercher dans les inputs de la section
    for (const [key, inputData] of Object.entries(sectionInputs)) {
      console.log('Vérification de l\'input:', key, 'avec data:', inputData);
      
      // Extraire la valeur selon le format
      let value = inputData;
      let label = '';
      if (typeof inputData === 'object' && inputData !== null) {
        if (inputData.value !== undefined) {
        value = inputData.value;
      }
        if (inputData.label !== undefined) {
          label = inputData.label;
        }
      }
      
      // Vérifier si c'est un input de surface (par clé ou par label)
      const keyLower = key.toLowerCase();
      const labelLower = label.toLowerCase();
      const isSurfaceInput = keywords.some(keyword => 
        keyLower.includes(keyword) || labelLower.includes(keyword)
      );
      
      if (isSurfaceInput) {
        const numericValue = parseFloat(value);
        console.log('Surface trouvée:', numericValue, 'dans la section:', sectionId, 'pour le type:', projectType);
        if (!isNaN(numericValue) && numericValue > 0) {
          return numericValue;
        }
      }
    }
  }
  
  console.log('Aucune surface trouvée pour le type:', projectType);
  return null;
};

/**
 * Calculs pour les projets de type TERASSE_EXTERIEUR
 * Utilise la surface renseignée dans la section "TAILLE DE VOTRE TERRASSE" si disponible
 */
export const calculateTerraceQuantity = (productType, surfaceArea) => {
  // Utiliser la surface renseignée ou une surface par défaut de 1 m²
  const area = surfaceArea || 1;

  // Normaliser les codes produits (enlever accents, espaces, ponctuation)
  const normalize = (value) => {
    if (!value) return '';
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // accents
      .replace(/['"’‘´`]/g, '')       // apostrophes/quotes
      .replace(/[^A-Z0-9]+/gi, '_')   // tout le reste en underscore
      .toUpperCase();
  };

  const code = normalize(productType);
  console.log('Calcul terrasse pour', productType, '→ code normalisé:', code, 'surface:', area);
  
  switch (code) {
    case 'LAMBOURDE':
      // Calcul selon la logique détaillée :
      // 1. ML total = m² terrasse x 2,5
      // 2. Nombre de lambourdes = ML total / longueur d'une lambourde (3 ML)
      // 3. Arrondir au-dessus
      const longueurLambourde = 3; // Longueur d'une lambourde en ML
      const mlTotal = area * 2.5;
      const nombreLambourdes = Math.ceil(mlTotal / longueurLambourde);
      return nombreLambourdes;
      
    case 'BANDE_BITUMINEUSE':
      // UN total = total ML de lambourde / 20 avec arrondi supérieur
      return Math.ceil((area * 2.5) / 20);
      
    case 'CALE_VENTILATION':
      // UN total = m² terrasse / 19 avec arrondi supérieur
      return Math.ceil(area / 19);
      
    case 'VISERIE':
      // BT total = m² terrasse x 40 / 400 avec arrondi supérieur
      return Math.ceil((area * 40) / 400);
      
    case 'PLOTS_DAKOTA':
    case 'PLOTS':
      // UN total = m² terrasse x 4
      return Math.ceil(area * 4);
      
    case 'CALE_DISOLATION':
    case 'CALE_D_ISOLATION':
    case 'CALE_DISOATION':
    case 'CALE_D_ISOATION':
      // BT total = m² terrasse x 4 / 25 avec arrondi supérieur
      return Math.ceil((area * 4) / 25);
      
    case 'GEOTEXTILE':
      // UN total = m² terrasse / 25 avec arrondi supérieur
      return Math.ceil(area / 25);
      
    case 'ESPACEUR_KIT_SERRE_LAME':
    case 'ESPACEURS_KIT_SERRE_LAME':
    case 'KIT_SERRE_LAME':
      // 1 UN
      return 1;

    case 'FORET_POUR_PRE_PERCAGE':
    case 'FORET_POUR_PRE_PERCAG':
    case 'FORET_POUR_PRE_PRECAGE':
      // UN total = m² terrasse / 70 avec arrondi supérieur
      return Math.ceil(area / 70);
      
    case 'TRAIT_DECOUP':
    case 'TRAITS_DECOUP':
      // 1 UN
      return 1;
      
    case 'LAMES_TERRASSE':
    case 'LAME_TERRASSE':
    case 'LAME':
      // Calcul selon la logique : 
      // 1. Nombre théorique = surface ÷ surface d'une lame (0,435 m²)
      // 2. Avec 7% de chute = nombre théorique × 1,07
      // 3. Arrondir au-dessus
      const surfaceParLame = 0.435; // Surface d'une lame en m²
      const nombreTheorique = area / surfaceParLame;
      const nombreAvecChute = nombreTheorique * 1.07;
      return Math.ceil(nombreAvecChute);
      
    default:
      console.warn('Code produit terrasse non géré:', productType, '(normalisé:', code, ')');
      return 1;
  }
};

/**
 * Récupère les paramètres de calcul pour BARDAGE depuis les inputs utilisateur et les choix
 */
const getBardageParams = (userInputs, selectedChoices = []) => {
  const params = {
    orientation: null, // 'HORIZONTALE' ou 'VERTICALE'
    surfaceArea: null,
    lamesType: null, // Type de lames sélectionné
    hasColor: false,
    colorCode: null
  };

  // Parcourir les choix sélectionnés pour déterminer l'orientation et le type
  selectedChoices.forEach(choice => {
    const code = choice.code || '';
    
    // Orientation
    if (code === 'SENS_POSE_HORIZONTALE' || code.includes('HORIZONTALE')) {
      params.orientation = 'HORIZONTALE';
    } else if (code === 'SENS_POSE_VERTICALE' || code.includes('VERTICALE')) {
      params.orientation = 'VERTICALE';
    }
    
    // Type de bardage
    if (code === 'TYPE_LAMES_BOIS') {
      params.lamesType = 'LAMES_BOIS';
    } else if (code === 'TYPE_LAMES_FIBRES_CIMENT') {
      params.lamesType = 'LAMES_FIBRES_CIMENT';
    } else if (code === 'TYPE_PANNEAUX_FIBRES_CIMENT') {
      params.lamesType = 'PANNEAUX_FIBRES_CIMENT';
    }
  });

  // Parcourir tous les inputs pour extraire la surface
  for (const [sectionId, sectionInputs] of Object.entries(userInputs)) {
    for (const [key, inputData] of Object.entries(sectionInputs)) {
      const value = typeof inputData === 'object' && inputData?.value !== undefined 
        ? inputData.value 
        : inputData;
      const label = typeof inputData === 'object' && inputData?.label 
        ? inputData.label.toLowerCase() 
        : key.toLowerCase();

      // Surface
      if (label.includes('surface') || label.includes('m²') || label.includes('m2') || label.includes('bardage')) {
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue) && numericValue > 0) {
          params.surfaceArea = numericValue;
        }
      }
    }
  }

  return params;
};

/**
 * Calculs pour les projets de type BARDAGE
 * Formules basées sur le PDF "SITE WEB RULLIER - Projet en 1 click - BARDAGE.pdf"
 * Gère l'orientation (HORIZONTALE/VERTICALE) et les codes produits spécifiques
 */
const calculateBardageQuantity = (productCode, surfaceArea, userInputs = {}, selectedChoices = []) => {
  const params = getBardageParams(userInputs, selectedChoices);
  const area = params.surfaceArea || surfaceArea || 1;
  
  // Utiliser le code produit (SKU) si disponible, sinon productType
  const code = String(productCode).toUpperCase().trim();
  
  // Déterminer le type de bardage
  const bardageType = selectedChoices.find(c => 
    c.code === 'TYPE_LAMES_BOIS' || 
    c.code === 'TYPE_LAMES_FIBRES_CIMENT' || 
    c.code === 'TYPE_PANNEAUX_FIBRES_CIMENT'
  )?.code;

  // Calculs selon l'orientation et le type de bardage
  switch (code) {
    case 'LAMBOURDE':
    case 'LAMBOURDES':
      // Lambourde : selon l'orientation
      // HORIZONTALE: structure simple, VERTICALE: structure double
      if (params.orientation === 'VERTICALE') {
        return Math.ceil(area * 2.0); // Structure double
      } else {
        return Math.ceil(area * 1.5); // Structure simple (HORIZONTALE par défaut)
      }
      
    case 'VISERIE':
    case 'VIS':
    case 'VISSERIE':
      // Visserie : selon l'orientation
      if (params.orientation === 'VERTICALE') {
        return Math.ceil(area * 25);
      } else {
      return Math.ceil(area * 20);
      }
      
    case 'BARDAGE_LAMES_BOIS':
    case 'LAMES_BOIS':
      // Lames de bardage bois : Surface + 7% (taux de chute)
      return Math.ceil(area * 1.07);
      
    case 'BARDAGE_LAMES_FIBRES_CIMENT':
    case 'LAMES_FIBRES_CIMENT':
      // Lames de bardage fibres-ciment : Surface + 5% (taux de chute)
      return Math.ceil(area * 1.05);
      
    case 'BARDAGE_PANNEAUX_FIBRES_CIMENT':
    case 'PANNEAUX_FIBRES_CIMENT':
      // Panneaux de bardage fibres-ciment : Surface + 5% (taux de chute)
      return Math.ceil(area * 1.05);
      
    case 'ECRAN_PLUIE':
    case 'ECRAN_DE_PLUIE':
      // Écran de pluie : Surface (m²)
      return Math.ceil(area);
      
    case 'EPDM':
      // EPDM : Surface (m²)
      return Math.ceil(area);
      
    case 'CORNIERE':
    case 'CORNIE RES':
      // Cornière : Périmètre (ML) - estimation basée sur la surface
      // Périmètre ≈ 4 * √surface
      const perimetreCorniere = 4 * Math.sqrt(area);
      return Math.ceil(perimetreCorniere);
      
    case 'GRILLE_VENTILATION':
    case 'GRILLE_DE_VENTILATION':
      // Grille de ventilation : 1 par 10 m²
      return Math.max(1, Math.ceil(area / 10));
      
    case 'PROFIL_DEPART':
    case 'PROFIL_DE_DEPART':
      // Profil de départ : Périmètre (ML)
      const perimetreDepart = 4 * Math.sqrt(area);
      return Math.ceil(perimetreDepart);
      
    case 'PROFIL_FIN':
    case 'PROFIL_DE_FIN':
      // Profil de fin : Périmètre (ML)
      const perimetreFin = 4 * Math.sqrt(area);
      return Math.ceil(perimetreFin);
      
    case 'PEINTURE_RETOUCHE':
    case 'PEINTURE_DE_RETOUCHE':
      // Peinture de retouche : 1 UN (unité)
      return 1;
      
    default:
      // Pour les produits non reconnus, retourner 1
      console.log(`Code produit BARDAGE non géré: ${code}, surface: ${area}`);
      return 1;
  }
};

/**
 * Calculs pour les projets de type SOL
 * Formules basées sur le PDF "SITE WEB RULLIER - 1 CLICK SOL - FIN.pdf"
 */
const calculateSolQuantity = (productType, surfaceArea, userInputs = {}, selectedChoices = []) => {
  const area = surfaceArea || 1;
  
  // Déterminer le type de sol sélectionné
  const solType = selectedChoices.find(c => 
    c.code === 'TYPE_PARQUET' || c.code === 'TYPE_VINYLE' || c.code === 'TYPE_STRATIFIE'
  )?.code;
  
  // Normaliser le code produit pour la comparaison
  const normalizedProductType = String(productType).toUpperCase().trim();
  
  switch (normalizedProductType) {
    case 'LAMBOURDE':
    case 'LAMBOURDES':
      // Lambourde : Surface x 2 (mètre linéaire)
      return Math.ceil(area * 2);
      
    case 'VISERIE':
    case 'VIS':
    case 'VISSERIE':
      // Visserie : Surface x 30 (unité)
      return Math.ceil(area * 30);
      
    case 'SOUS_COUCHE':
    case 'SOUSCOUCHE':
    case 'SOUS-COUCHE':
      // Sous-couche : Surface (m²) - pas de taux de chute
      return Math.ceil(area);
      
    case 'PLINTHE':
    case 'PLINTHES':
      // Plinthe : Périmètre (ML) - à calculer depuis les dimensions si disponibles
      // Pour l'instant, estimation basée sur la surface : périmètre ≈ 4 * √surface
      const perimetre = 4 * Math.sqrt(area);
      return Math.ceil(perimetre);
      
    case 'PARQUET':
      // Parquet : Surface + 7% (taux de chute)
      return Math.ceil(area * 1.07);
      
    case 'VINYLE':
    case 'VINYLE_SOL':
      // Vinyle : Surface + 5% (taux de chute)
      return Math.ceil(area * 1.05);
      
    case 'STRATIFIE':
    case 'STRATIFIE_SOL':
      // Stratifié : Surface + 7% (taux de chute)
      return Math.ceil(area * 1.07);
      
    case 'BARRE_DE_SEUIL':
    case 'BARRE_SEUIL':
    case 'SEUIL':
      // Barre de seuil : 1 par porte/ouverture (à estimer)
      // Estimation : 1 barre par 20 m²
      return Math.max(1, Math.ceil(area / 20));
      
    case 'PROFIL_DE_FINITION':
    case 'PROFIL_FINITION':
      // Profil de finition : Périmètre / longueur standard (2.5m)
      const perimetreProfil = 4 * Math.sqrt(area);
      return Math.ceil(perimetreProfil / 2.5);
      
    case 'COLLANT':
    case 'COLLE':
      // Colle : Surface (m²) - pour pose collée uniquement
      // Vérifier si pose collée est sélectionnée
      const poseCollee = selectedChoices.find(c => c.code === 'POSE_COLLEE');
      if (poseCollee) {
        return Math.ceil(area);
      }
      return 0;
      
    case 'FILM_POLYETHYLENE':
    case 'FILM_PE':
    case 'FILM_POLYETHYLENE':
      // Film polyéthylène : Surface (m²) - pour pose flottante
      const poseFlottante = selectedChoices.find(c => c.code === 'POSE_FLOTTANTE');
      if (poseFlottante || solType === 'TYPE_VINYLE' || solType === 'TYPE_STRATIFIE') {
        return Math.ceil(area);
      }
      return 0;
      
    case 'ISOLANT_ACOUSTIQUE':
    case 'ISOLANT':
      // Isolant acoustique : Surface (m²) - pour pose flottante
      const poseFlottanteIsolant = selectedChoices.find(c => c.code === 'POSE_FLOTTANTE');
      if (poseFlottanteIsolant || solType === 'TYPE_VINYLE' || solType === 'TYPE_STRATIFIE') {
        return Math.ceil(area);
      }
      return 0;
      
    default:
      // Pour les autres produits, retourner 1 par défaut
      return 1;
  }
};

/**
 * Récupère les paramètres de calcul pour MUR_PLAQUE_PLATRE depuis les inputs utilisateur et les choix sélectionnés
 */
const getMurPlaquePlatreParams = (userInputs, selectedChoices = []) => {
  const params = {
    projectSubType: null, // 'DOUBLAGE' ou 'CLOISON'
    plafondType: null, // 'PLAT' ou 'RAMPANT'
    hauteurSousPlafond: null,
    hauteurBassePlafond: null,
    hauteurHautePlafond: null,
    metreLineaire: null,
    longueurTotale: null,
    nombreFenetres: 0,
    nombrePortes: 0,
    nombreBaies: 0,
    nombreVerrieres: 0,
    nombrePignons: 0,
    largeurMoyennePignons: null,
    isolationType: null,
    isolationEpaisseur: null,
    surfaceMenuiseries: null,
    // Pour CLOISON+ (portes)
    portes: []
  };

  // D'abord, déterminer le type de projet et le type de plafond depuis les choix sélectionnés
  selectedChoices.forEach(choice => {
    const code = choice.code || '';
    
    // Type de projet
    if (code === 'TYPE_DOUBLAGE') {
      params.projectSubType = 'DOUBLAGE';
    } else if (code === 'TYPE_CLOISON') {
      params.projectSubType = 'CLOISON';
    }
    
    // Type de plafond
    if (code === 'PLAFOND_PLAT') {
      params.plafondType = 'PLAT';
    } else if (code === 'PLAFOND_RAMPANT') {
      params.plafondType = 'RAMPANT';
    }
  });

  // Parcourir tous les inputs pour extraire les paramètres
  for (const [sectionId, sectionInputs] of Object.entries(userInputs)) {
    for (const [key, inputData] of Object.entries(sectionInputs)) {
      const value = typeof inputData === 'object' && inputData?.value !== undefined 
        ? inputData.value 
        : inputData;
      const label = typeof inputData === 'object' && inputData?.label 
        ? inputData.label.toLowerCase() 
        : key.toLowerCase();

      // Type de projet (fallback si pas trouvé dans les choix)
      if (!params.projectSubType) {
        if (label.includes('doublage')) params.projectSubType = 'DOUBLAGE';
        if (label.includes('cloison')) params.projectSubType = 'CLOISON';
      }

      // Type de plafond (fallback si pas trouvé dans les choix)
      if (!params.plafondType) {
        if (label.includes('plafond plat') || label.includes('plafond_plat')) params.plafondType = 'PLAT';
        if (label.includes('plafond rampant') || label.includes('plafond_rampant')) params.plafondType = 'RAMPANT';
      }

      // Hauteurs - Plus flexible pour détecter les variations
      if ((label.includes('hauteur') && label.includes('sous') && label.includes('plafond')) ||
          label.includes('hauteur sous plafond') || 
          label.includes('hauteur_sous_plafond') ||
          label.includes('hauteur sous le plafond')) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue > 0) {
          params.hauteurSousPlafond = numValue;
          console.log(`✅ Hauteur sous plafond détectée: ${numValue} (label: "${label}")`);
        }
      }
      if ((label.includes('hauteur') && label.includes('basse')) || 
          label.includes('hauteur_basse')) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue > 0) {
          params.hauteurBassePlafond = numValue;
          console.log(`✅ Hauteur basse plafond détectée: ${numValue}`);
        }
      }
      if ((label.includes('hauteur') && label.includes('haute')) || 
          label.includes('hauteur_haute')) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue > 0) {
          params.hauteurHautePlafond = numValue;
          console.log(`✅ Hauteur haute plafond détectée: ${numValue}`);
        }
      }

      // Longueurs - Plus flexible
      if ((label.includes('mètre') || label.includes('metre') || label.includes('ml')) && 
          (label.includes('linéaire') || label.includes('lineaire') || label.includes('total'))) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue > 0) {
          params.metreLineaire = numValue;
          console.log(`✅ Mètre linéaire détecté: ${numValue} (label: "${label}")`);
        }
      }
      if ((label.includes('longueur') && label.includes('totale')) || 
          label.includes('longueur_totale')) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue > 0) {
          params.longueurTotale = numValue;
          console.log(`✅ Longueur totale détectée: ${numValue}`);
        }
      }

      // Menuiseries - Surface totale directement (plus flexible)
      if ((label.includes('surface') && label.includes('totale') && label.includes('menuiseries')) ||
          (label.includes('surface') && label.includes('menuiseries')) ||
          (label.includes('menuiseries') && (label.includes('m²') || label.includes('m2') || label.includes('surface')))) {
        const surfaceValue = parseFloat(value);
        if (!isNaN(surfaceValue) && surfaceValue > 0) {
          params.surfaceMenuiseries = surfaceValue;
          console.log(`✅ Surface menuiseries détectée: ${surfaceValue} m² (label: "${label}")`);
        }
      }
      
      // Menuiseries - Nombre d'éléments (plus flexible)
      if ((label.includes('nombre') || label.includes('nb')) && 
          (label.includes('fenêtre') || label.includes('fenetre'))) {
        const numValue = parseInt(value) || 0;
        if (numValue > 0) {
          params.nombreFenetres = numValue;
          console.log(`✅ Nombre de fenêtres détecté: ${numValue}`);
        }
      }
      if ((label.includes('nombre') || label.includes('nb')) && 
          label.includes('porte') && !label.includes('type') && !label.includes('surface')) {
        const numValue = parseInt(value) || 0;
        if (numValue > 0) {
          params.nombrePortes = numValue;
          console.log(`✅ Nombre de portes détecté: ${numValue}`);
        }
      }
      if ((label.includes('nombre') || label.includes('nb')) && 
          (label.includes('baie') || label.includes('baies'))) {
        const numValue = parseInt(value) || 0;
        if (numValue > 0) {
          params.nombreBaies = numValue;
          console.log(`✅ Nombre de baies détecté: ${numValue}`);
        }
      }
      if ((label.includes('nombre') || label.includes('nb')) && 
          (label.includes('verrière') || label.includes('verriere'))) {
        const numValue = parseInt(value) || 0;
        if (numValue > 0) {
          params.nombreVerrieres = numValue;
          console.log(`✅ Nombre de verrières détecté: ${numValue}`);
        }
      }

      // Pignons (pour plafond rampant)
      if (label.includes('pignon')) {
        params.nombrePignons = parseInt(value) || 0;
      }
      if (label.includes('largeur moyenne') || label.includes('largeur_moyenne')) {
        params.largeurMoyennePignons = parseFloat(value);
      }

      // Isolation
      if (label.includes('isolation') || label.includes('isolant')) {
        if (label.includes('type')) {
          params.isolationType = value;
        }
        if (label.includes('épaisseur') || label.includes('epaisseur') || label.includes('mm')) {
          params.isolationEpaisseur = parseFloat(value);
        }
      }
    }
  }

  // Calculer la surface des menuiseries si nécessaire (seulement si pas déjà définie directement)
  if (!params.surfaceMenuiseries && 
      (params.nombreFenetres > 0 || params.nombrePortes > 0 || params.nombreBaies > 0 || params.nombreVerrieres > 0)) {
    params.surfaceMenuiseries = 
      (params.nombreFenetres * 1.250) +
      (params.nombrePortes * 1.935) +
      (params.nombreBaies * 6.45) +
      (params.nombreVerrieres * 1.300);
    console.log(`✅ Surface menuiseries calculée: ${params.surfaceMenuiseries} m²`);
  }

  // Debug: Afficher tous les paramètres détectés
  console.log('🔍 Paramètres MUR_PLAQUE_PLATRE détectés:', {
    projectSubType: params.projectSubType,
    plafondType: params.plafondType,
    hauteurSousPlafond: params.hauteurSousPlafond,
    hauteurBassePlafond: params.hauteurBassePlafond,
    hauteurHautePlafond: params.hauteurHautePlafond,
    metreLineaire: params.metreLineaire,
    longueurTotale: params.longueurTotale,
    surfaceMenuiseries: params.surfaceMenuiseries,
    nombreFenetres: params.nombreFenetres,
    nombrePortes: params.nombrePortes,
    nombreBaies: params.nombreBaies,
    nombreVerrieres: params.nombreVerrieres
  });

  return params;
};

/**
 * Exporte la fonction pour calculer la surface DOUBLAGE
 */
export const getMurPlaquePlatreSurface = (userInputs = {}, selectedChoices = []) => {
  const params = getMurPlaquePlatreParams(userInputs, selectedChoices);
  
  if (params.projectSubType === 'DOUBLAGE') {
    return calculateSurfaceDoublage(params);
  } else if (params.projectSubType === 'CLOISON') {
    return calculateSurfaceCloison(params);
  }
  
  return 0;
};

/**
 * Calcule la surface pour DOUBLAGE
 */
const calculateSurfaceDoublage = (params) => {
  let surface = 0;

  // Si on a hauteur sous plafond et mètre linéaire, on peut calculer même sans type de plafond explicite
  // On assume PLAT par défaut si on a hauteurSousPlafond
  if (params.plafondType === 'PLAT' || (!params.plafondType && params.hauteurSousPlafond)) {
    // Surface = (Hauteur sous plafond) x (Mètre linéaire de projet) - (Surface prise par les menuiseries)
    surface = (params.hauteurSousPlafond || 0) * (params.metreLineaire || 0);
    console.log(`📐 Calcul DOUBLAGE (PLAT): ${params.hauteurSousPlafond} x ${params.metreLineaire} = ${surface} m²`);
  } else if (params.plafondType === 'RAMPANT') {
    // Surface = h (Hauteur basse plafond) x (Mètre linéaire de projet)
    // + (Nombre de pignons) x [H - h] x (largeur moyenne des pignons) x 0,5
    // - (Surface prise par les menuiseries)
    const h = params.hauteurBassePlafond || 0;
    const H = params.hauteurHautePlafond || 0;
    const ml = params.metreLineaire || 0;
    const pignons = params.nombrePignons || 0;
    const largeurPignons = params.largeurMoyennePignons || 0;

    surface = (h * ml) + (pignons * (H - h) * largeurPignons * 0.5);
    console.log(`📐 Calcul DOUBLAGE (RAMPANT): ${h} x ${ml} + (${pignons} x (${H} - ${h}) x ${largeurPignons} x 0.5) = ${surface} m²`);
  }

  // Soustraire la surface des menuiseries
  if (params.surfaceMenuiseries) {
    const surfaceAvant = surface;
    surface -= params.surfaceMenuiseries;
    console.log(`📐 Surface avant menuiseries: ${surfaceAvant} m², après: ${surface} m² (soustrait ${params.surfaceMenuiseries} m²)`);
  }

  return Math.max(0, surface);
};

/**
 * Calcule la surface pour CLOISON
 */
const calculateSurfaceCloison = (params) => {
  let surface = 0;

  // Si on a hauteur sous plafond et longueur totale, on peut calculer même sans type de plafond explicite
  // On assume PLAT par défaut si on a hauteurSousPlafond
  if (params.plafondType === 'PLAT' || (!params.plafondType && params.hauteurSousPlafond)) {
    // Surface' = (Hauteur sous plafond) x (Longueur totale de cloison) - (Surface prise par les menuiseries)
    surface = (params.hauteurSousPlafond || 0) * (params.longueurTotale || 0);
    console.log(`📐 Calcul CLOISON (PLAT): ${params.hauteurSousPlafond} x ${params.longueurTotale} = ${surface} m²`);
  } else if (params.plafondType === 'RAMPANT') {
    // Surface'' = h (Hauteur basse plafond) x (Longueur totale cloison)
    // + [H - h] x (Longueur totale cloison) x 0,5
    // - (Surface prise par les menuiseries)
    const h = params.hauteurBassePlafond || 0;
    const H = params.hauteurHautePlafond || 0;
    const longueur = params.longueurTotale || 0;

    surface = (h * longueur) + ((H - h) * longueur * 0.5);
    console.log(`📐 Calcul CLOISON (RAMPANT): ${h} x ${longueur} + ((${H} - ${h}) x ${longueur} x 0.5) = ${surface} m²`);
  }

  // Soustraire la surface des menuiseries
  if (params.surfaceMenuiseries) {
    const surfaceAvant = surface;
    surface -= params.surfaceMenuiseries;
    console.log(`📐 Surface avant menuiseries: ${surfaceAvant} m², après: ${surface} m² (soustrait ${params.surfaceMenuiseries} m²)`);
  }

  return Math.max(0, surface);
};

/**
 * Calculs pour les projets de type MUR_PLAQUE_PLATRE
 * Gère DOUBLAGE et CLOISON avec leurs formules spécifiques
 */
const calculateMurPlaquePlatreQuantity = (productType, surfaceArea, userInputs = {}, selectedChoices = []) => {
  const params = getMurPlaquePlatreParams(userInputs, selectedChoices);
  
  // Déterminer la surface selon le type de projet
  let surface = 0;
  if (params.projectSubType === 'DOUBLAGE') {
    surface = calculateSurfaceDoublage(params);
  } else if (params.projectSubType === 'CLOISON') {
    surface = calculateSurfaceCloison(params);
  } else {
    // Fallback sur la surface fournie
    surface = surfaceArea || 1;
  }

  console.log('Calcul MUR_PLAQUE_PLATRE:', {
    productType,
    projectSubType: params.projectSubType,
    surface,
    params
  });

  // Mapping des codes produits vers les calculs
  const productCode = productType; // Le productType devrait contenir le code produit

  // Calculs pour DOUBLAGE
  if (params.projectSubType === 'DOUBLAGE') {
    switch (productCode) {
      case '334170':
        // ARRONDI.SUP (Surface x 1,05 / 3)
        return Math.ceil((surface * 1.05) / 3);
      case '240503':
        // ARRONDI.SUP (Surface x 2,40 / 5,30)
        return Math.ceil((surface * 2.40) / 5.30);
      case '322179':
        // ARRONDI.SUP (Surface x 1,10 / 3)
        return Math.ceil((surface * 1.10) / 3);
      case '247942':
        // ARRONDI.SUP (Surface x 11 / 1000)
        return Math.ceil((surface * 11) / 1000);
      case '247854':
        // ARRONDI.SUP (Surface x 1,40 / 150)
        return Math.ceil((surface * 1.40) / 150);
      case '335302':
        // ARRONDI.SUP (Surface x 0,47 / 22)
        return Math.ceil((surface * 0.47) / 22);
      
      // Isolation DOUBLAGE
      case '335138':
        // Si épaisseur 100mm
        if (params.isolationEpaisseur === 100) {
          return Math.ceil(surface / 50);
        }
        return 0;
      case '335141':
        // Si épaisseur 120 à 180mm
        if (params.isolationEpaisseur >= 120 && params.isolationEpaisseur <= 180) {
          return Math.ceil(surface / 50);
        }
        return 0;
      case '339736':
        // Paille de riz 100mm ou 145mm
        if (params.isolationType?.toLowerCase().includes('paille') && 
            (params.isolationEpaisseur === 100 || params.isolationEpaisseur === 145)) {
          return Math.ceil(surface / 0.696);
        }
        return 0;
      case '246493':
        // Laine de roche 120mm
        if (params.isolationType?.toLowerCase().includes('roche') && params.isolationEpaisseur === 120) {
          return Math.ceil(surface / 3.810);
        }
        return 0;
      case '336986':
        // Laine de roche 145mm
        if (params.isolationType?.toLowerCase().includes('roche') && params.isolationEpaisseur === 145) {
          return Math.ceil(surface / 3.051);
        }
        return 0;
      case '326190':
        // Fibre de bois 100mm
        if (params.isolationType?.toLowerCase().includes('fibre') && params.isolationEpaisseur === 100) {
          return Math.ceil(surface / 4.246);
        }
        return 0;
      case '326191':
        // Fibre de bois 120mm
        if (params.isolationType?.toLowerCase().includes('fibre') && params.isolationEpaisseur === 120) {
          return Math.ceil(surface / 3.538);
        }
        return 0;
      case '326192':
        // Fibre de bois 145mm
        if (params.isolationType?.toLowerCase().includes('fibre') && params.isolationEpaisseur === 145) {
          return Math.ceil(surface / 2.830);
        }
        return 0;
      case '326193':
        // Fibre de bois 160mm
        if (params.isolationType?.toLowerCase().includes('fibre') && params.isolationEpaisseur === 160) {
          return Math.ceil(surface / 2.830);
        }
        return 0;
      case '329985':
        // Laine de verre 100mm
        if (params.isolationType?.toLowerCase().includes('verre') && params.isolationEpaisseur === 100) {
          return Math.ceil(surface / 4.050);
        }
        return 0;
      case '329984':
        // Laine de verre 120mm
        if (params.isolationType?.toLowerCase().includes('verre') && params.isolationEpaisseur === 120) {
          return Math.ceil(surface / 3.240);
        }
        return 0;
      case '329983':
        // Laine de verre 140mm
        if (params.isolationType?.toLowerCase().includes('verre') && params.isolationEpaisseur === 140) {
          return Math.ceil(surface / 3.240);
        }
        return 0;
      case '329976':
        // Laine de verre 160mm
        if (params.isolationType?.toLowerCase().includes('verre') && params.isolationEpaisseur === 160) {
          return Math.ceil(surface / 3.240);
        }
        return 0;
    }
  }

  // Calculs pour CLOISON
  if (params.projectSubType === 'CLOISON') {
    switch (productCode) {
      case '334170':
        // ARRONDI.SUP (Surface x 2,10 / 3)
        return Math.ceil((surface * 2.10) / 3);
      case '247942':
        // ARRONDI.SUP (Surface x 22 / 1000)
        return Math.ceil((surface * 22) / 1000);
      case '247854':
        // ARRONDI.SUP (Surface x 2,80 / 150)
        return Math.ceil((surface * 2.80) / 150);
      case '335302':
        // ARRONDI.SUP (Surface x 0,94 / 22)
        return Math.ceil((surface * 0.94) / 22);
      
      // Isolation CLOISON
      case '240474':
        // Si épaisseur 45mm ou si pas d'isolant
        if (!params.isolationType || params.isolationEpaisseur === 45) {
          return Math.ceil((surface * 2.00) / 2.80);
        }
        return 0;
      case '240483':
        // Si épaisseur 70mm
        if (params.isolationEpaisseur === 70) {
          return Math.ceil((surface * 2.00) / 2.80);
        }
        return 0;
      case '240500':
        // Si épaisseur 100mm
        if (params.isolationEpaisseur === 100) {
          return Math.ceil((surface * 2.00) / 3.00);
        }
        return 0;
      case '240468':
        // Si épaisseur 45mm ou si pas d'isolant
        if (!params.isolationType || params.isolationEpaisseur === 45) {
          return Math.ceil((surface * 0.90) / 3.00);
        }
        return 0;
      case '240469':
        // Si épaisseur 70mm
        if (params.isolationEpaisseur === 70) {
          return Math.ceil((surface * 0.90) / 3.00);
        }
        return 0;
      case '240498':
        // Si épaisseur 100mm
        if (params.isolationEpaisseur === 100) {
          return Math.ceil((surface * 0.90) / 3.00);
        }
        return 0;
      case '339736':
        // Paille de riz 100mm
        if (params.isolationType?.toLowerCase().includes('paille') && params.isolationEpaisseur === 100) {
          return Math.ceil(surface / 0.696);
        }
        return 0;
      case '236198':
        // Laine de roche 40mm
        if (params.isolationType?.toLowerCase().includes('roche') && params.isolationEpaisseur === 40) {
          return Math.ceil(surface / 6.000);
        }
        return 0;
      case '326187':
        // Fibre de bois 40mm
        if (params.isolationType?.toLowerCase().includes('fibre') && params.isolationEpaisseur === 40) {
          return Math.ceil(surface / 10.614);
        }
        return 0;
      case '326188':
        // Fibre de bois 60mm
        if (params.isolationType?.toLowerCase().includes('fibre') && params.isolationEpaisseur === 60) {
          return Math.ceil(surface / 7.076);
        }
        return 0;
      case '326190':
        // Fibre de bois 100mm
        if (params.isolationType?.toLowerCase().includes('fibre') && params.isolationEpaisseur === 100) {
          return Math.ceil(surface / 4.260);
        }
        return 0;
      case '329975':
        // Laine de verre 45mm
        if (params.isolationType?.toLowerCase().includes('verre') && params.isolationEpaisseur === 45) {
          return Math.ceil(surface / 19.200);
        }
        return 0;
      case '329974':
        // Laine de verre 70mm
        if (params.isolationType?.toLowerCase().includes('verre') && params.isolationEpaisseur === 70) {
          return Math.ceil(surface / 12.600);
        }
        return 0;
      case '329985':
        // Laine de verre 100mm
        if (params.isolationType?.toLowerCase().includes('verre') && params.isolationEpaisseur === 100) {
          return Math.ceil(surface / 4.050);
  }
        return 0;
    }
  }

  // Gestion des portes pour CLOISON+ (Menuiserie)
  // Si présence de porte est > 0, multiplier les codes suivants par le nombre de portes
  if (params.projectSubType === 'CLOISON' && params.nombrePortes > 0) {
    // Mapping des codes de portes selon type, épaisseur et sens d'ouverture
    const porteCodes = {
      // Poussant Gauche
      'poussant_gauche_lisse_73': '023118',
      'poussant_gauche_lisse_83': '023119',
      'poussant_gauche_lisse_93': '024313',
      'poussant_gauche_confort_73': '023122',
      'poussant_gauche_confort_83': '023123',
      'poussant_gauche_confort_93': '024350',
      'poussant_gauche_deco_73': '334050',
      'poussant_gauche_deco_83': '334052',
      'poussant_gauche_technique_73': '037465',
      'poussant_gauche_technique_83': '037467',
      'poussant_gauche_technique_93': '037740',
      // Poussant Droit
      'poussant_droit_lisse_73': '014059',
      'poussant_droit_lisse_83': '014060',
      'poussant_droit_lisse_93': '024312',
      'poussant_droit_confort_73': '014063',
      'poussant_droit_confort_83': '014064',
      'poussant_droit_confort_93': '024349',
      'poussant_droit_deco_73': '334049',
      'poussant_droit_deco_83': '334051',
      'poussant_droit_technique_73': '037464',
      'poussant_droit_technique_83': '037466',
      'poussant_droit_technique_93': '037739'
    };

    // Vérifier si le code correspond à une porte
    const porteKey = Object.keys(porteCodes).find(key => porteCodes[key] === productCode);
    if (porteKey) {
      // Multiplier par le nombre de portes
      return params.nombrePortes;
    }
  }

  // Fallback pour les anciens codes ou codes non reconnus
  console.warn(`Code produit non géré pour MUR_PLAQUE_PLATRE: ${productCode}, surface: ${surface}`);
  return 1;
};

/**
 * Calcule le prix total pour un produit avec sa quantité calculée
 * @param {Object} product - Produit avec price et sku
 * @param {string} productType - Type du produit
 * @param {string} projectType - Type de projet
 * @param {number} surfaceArea - Surface en m²
 * @param {Object} userInputs - Inputs utilisateur
 * @param {Array} selectedChoices - Choix sélectionnés (pour BARDAGE notamment)
 * @returns {Object} { quantity, totalPrice }
 */
export const calculateProductPricing = (product, productType, projectType, surfaceArea, userInputs, selectedChoices = []) => {
  // Pour MUR_PLAQUE_PLATRE et BARDAGE, utiliser le SKU (code produit) si disponible, sinon productType
  let identifier = productType;
  if ((projectType === 'MUR_PLAQUE_PLATRE' || projectType === 'BARDAGE') && product?.sku) {
    identifier = product.sku;
  }
  
  const calculatedQuantity = calculateProductQuantity(identifier, projectType, surfaceArea, userInputs, selectedChoices);
  const unitPrice = parseFloat(product.price) || 0;
  const totalPrice = calculatedQuantity * unitPrice;
  
  return {
    quantity: calculatedQuantity,
    totalPrice: totalPrice
  };
};

/**
 * Applique le taux de chute pour les lames de terrasse (+7%)
 * @param {number} baseQuantity - Quantité de base
 * @param {string} productType - Type du produit
 * @returns {number} Quantité avec taux de chute
 */
export const applyWasteRate = (baseQuantity, productType) => {
  if (!productType) return baseQuantity;
  // Appliquer le taux de chute uniquement pour les lames de terrasse
  if (productType === 'LAMES_TERRASSE' || productType.toLowerCase().includes('lame')) {
    return Math.ceil(baseQuantity * 1.07); // +7%
  }
  return baseQuantity;
};
